import type { FC, ReactNode } from 'react'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react'
import { audioEngine } from '@/lib/audio/engine'
import { setMotionEasing } from '@/lib/motion'

type Experience = {
    ready: boolean
    reducedMotion: boolean
    threeD: boolean
    sfx: boolean
    immersive: boolean
    pinned: boolean
    heroPinned: boolean
    introDone: boolean
    menuOpen: boolean
    setThreeD: (value: boolean) => void
    setSfx: (value: boolean) => void
    setIntroDone: (value: boolean) => void
    setMenuOpen: (value: boolean) => void
}

interface ExperienceProviderProps {
    children: ReactNode
}

const THREE_D_KEY = 'bluntsheep:3d'
const SFX_KEY = 'bluntsheep:sfx'
const PIN_QUERY = '(min-width: 60rem) and (min-height: 45rem)'
const TALL_QUERY = '(min-height: 38rem)'

const ExperienceContext = createContext<Experience | null>(null)

const readFlag = (key: string): boolean | null => {
    try {
        const value = window.localStorage.getItem(key)
        if (value === '1') return true
        if (value === '0') return false
    }
    catch {
        // Storage can be blocked in private modes; defaults apply.
    }

    return null
}

const writeFlag = (key: string, value: boolean): void => {
    try {
        window.localStorage.setItem(key, value ? '1' : '0')
    }
    catch {
        // Storage can be blocked in private modes; the choice lasts for this visit only.
    }
}

export const ExperienceProvider: FC<ExperienceProviderProps> = ({ children }): ReactNode => {
    const [ready, setReady] = useState(false)
    const [reducedMotion, setReducedMotion] = useState(false)
    const [threeD, setThreeDState] = useState(true)
    const [sfx, setSfxState] = useState(false)
    const [desktopPin, setDesktopPin] = useState(false)
    const [tall, setTall] = useState(true)
    const [introDone, setIntroDone] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)

    useEffect(() => {
        const storedThreeD = readFlag(THREE_D_KEY)
        const storedSfx = readFlag(SFX_KEY)

        if (storedThreeD !== null) setThreeDState(storedThreeD)
        if (storedSfx !== null) setSfxState(storedSfx)

        const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
        const pinQuery = window.matchMedia(PIN_QUERY)
        const tallQuery = window.matchMedia(TALL_QUERY)

        const syncMotion = () => setReducedMotion(motionQuery.matches)
        const syncPin = () => setDesktopPin(pinQuery.matches)
        const syncTall = () => setTall(tallQuery.matches)

        syncMotion()
        syncPin()
        syncTall()
        setReady(true)

        motionQuery.addEventListener('change', syncMotion)
        pinQuery.addEventListener('change', syncPin)
        tallQuery.addEventListener('change', syncTall)

        return () => {
            motionQuery.removeEventListener('change', syncMotion)
            pinQuery.removeEventListener('change', syncPin)
            tallQuery.removeEventListener('change', syncTall)
        }
    }, [])

    const immersive = !reducedMotion && threeD

    useEffect(() => {
        document.documentElement.dataset.motion = immersive ? 'full' : 'static'
        setMotionEasing(!reducedMotion)
    }, [immersive, reducedMotion])

    useEffect(() => {
        audioEngine.sfx = sfx
    }, [sfx])

    const setThreeD = useCallback((value: boolean) => {
        setThreeDState(value)
        writeFlag(THREE_D_KEY, value)
    }, [])

    const setSfx = useCallback((value: boolean) => {
        audioEngine.sfx = value
        setSfxState(value)
        writeFlag(SFX_KEY, value)

        // Turning sound on happens inside a tap, which is the moment browsers allow audio to start.
        if (value) audioEngine.cue('confirm')
    }, [])

    const value = useMemo<Experience>(() => ({
        ready,
        reducedMotion,
        threeD,
        sfx,
        immersive,
        pinned: immersive && desktopPin,
        heroPinned: immersive && tall,
        introDone,
        menuOpen,
        setThreeD,
        setSfx,
        setIntroDone,
        setMenuOpen
    }), [ready, reducedMotion, threeD, sfx, immersive, desktopPin, tall, introDone, menuOpen, setThreeD, setSfx])

    return (
        <ExperienceContext.Provider value={value}>
            {children}
        </ExperienceContext.Provider>
    )
}

export const useExperience = (): Experience => {
    const context = useContext(ExperienceContext)
    if (!context) throw new Error('useExperience must be used inside ExperienceProvider')

    return context
}
